/**
 * The Command Desk's right rail — §9, panel by panel.
 *
 * Eight panels in the order §2 fixes: Appointment Status → Alerts & Reminders → Inbox Monitor
 * → Useful Links → Tournament Officials → Cards Average Per Game → Pool Stage Progress →
 * Reports Outstanding. Three of them have no store in this build and each says so in its own
 * words rather than drawing a zero — a `0.0` in a stat box is a measurement, and there has
 * been no measurement.
 *
 * **RUN 82 (spec 01 §8a and §9a, amended 2026-09-17) — Cards Listing joins the rail**, straight
 * after Inbox Monitor, moved out of the left column by the Technical Delegate's Command Desk
 * addendum. It is still one of those three: no route serves the TMS cards report, so it is
 * still a named empty state, and its severity order is still the one it states.
 *
 * **RUN 86 - the two cards panels are drawn from the TMS cards report.** `GET /cards` serves both
 * competitions' public TMS reports as the server last read them (`TMS_CARDS_POLL=on`), and Cards
 * Listing and Cards Average Per Game read it through the page (`CommandDeskPage`), on the Desk's
 * five-minute refresh key. They are no longer among the panels with no store; each keeps its named
 * empty state for the time before a report has been read, and says why no report is there.
 *
 * **Fictional data only.** Nothing here holds a name.
 */

import { memo, useState } from "react";
import { Link } from "react-router-dom";

import type { CardsBody } from "../../api/types.js";
import { EmptyState, FailBanner, Skeleton } from "../../app/states.js";
import type { RefreshingAsyncState } from "../../app/useAsync.js";
import {
  cardsAverages,
  cardsListing,
  cardsTrend,
  competitionLabel,
  everyMinutes,
  oneDecimal,
  pauseLine,
  readProblem,
  reportAsOf,
  reportIsStale,
  sparkPoints,
} from "./cards.js";
import {
  ROLE_SEGMENTS,
  shortDate,
  type AppointmentWindow,
  type ApptStatus,
  type DayReadState,
  type DeskAlert,
  type DonutView,
  type PoolProgress,
} from "./derive.js";

/* ── §9 `Appointment Status` ─────────────────────────────────────────────────── */

/**
 * RUN 82J — the panel's line for window days whose appointments could not be read (third QA, M3):
 * `The appointments for Fri 18 Sep could not be read, so its matches are not counted here.`
 */
export function windowNotCounted(days: readonly string[]): string {
  return (
    `The appointments for ${days.map(shortDate).join(" and ")} could not be read, so ` +
    `${days.length === 1 ? "its" : "their"} matches are not counted here.`
  );
}

/**
 * §9's Appointment Status.
 *
 * **RUN 82J — two fixed days, each counted once it has been read (third QA, L5 and M3).**
 * `apptWindow` is `appointmentWindow`'s answer: the focus day and the next match day, whatever page
 * the lookahead is on, and of those only the days whose own appointments read has landed. The head
 * note names the days the figure counts. A day whose read was refused is said in a quiet `Not
 * Loaded` line — a `status`, as the Alerts panel's is, because the desk goes on drawing — and while
 * no day of the window has been read yet the figure is loading bars, never `0 of 0`.
 */
export function AppointmentStatusPanel({
  status,
  provisional,
  apptWindow,
}: {
  status: ApptStatus;
  provisional: readonly { matchNo: string; sides: string }[];
  /** RUN 82J — the two-day window, and which of its days the figure counts. */
  apptWindow: AppointmentWindow;
}): React.JSX.Element {
  const nothingCounted = apptWindow.days.length > 0 && apptWindow.counted.length === 0;
  return (
    <section className="panel panel--d2" aria-labelledby="apptTitle">
      <div className="panel__head">
        <h3 className="panel__title" id="apptTitle">
          Appointment Status
        </h3>
        <span className="spacer" />
        <span className="panel__note" data-testid="appt-window">
          {apptWindow.label}
        </span>
      </div>
      {apptWindow.refused.length === 0 ? null : (
        <FailBanner error={new Error(windowNotCounted(apptWindow.refused))} role="status" />
      )}
      {nothingCounted ? (
        apptWindow.loading.length === 0 ? null : <Skeleton rows={3} />
      ) : (
        <div className="completion">
          <div className="completion__figures">
            <span className="completion__big" data-testid="appt-complete">
              {status.complete}
            </span>
            <span className="completion__of">
              of {status.total} matches
              <br />
              technical crew complete
            </span>
            <span className="completion__pct num">{status.percent}%</span>
          </div>
          <div
            className="bar"
            role="progressbar"
            aria-valuenow={status.complete}
            aria-valuemin={0}
            aria-valuemax={status.total}
            aria-label={`${String(status.complete)} of ${String(status.total)} matches appointed`}
          >
            <div className="bar__fill" style={{ width: `${String(status.percent)}%` }} />
          </div>
          <div className="checks">
            <div
              className={status.conflicts.length === 0 ? "check check--good" : "check check--alert"}
            >
              <span className="glyph" aria-hidden="true">
                {status.conflicts.length === 0 ? "✓" : "✕"}
              </span>
              <span>
                {status.conflicts.length === 0 ? (
                  <>
                    Country conflicts: <b>none detected</b> in appointed crews
                  </>
                ) : (
                  <>
                    Country conflicts: <b>{status.conflicts.length}</b> —{" "}
                    <span className="mono">{status.conflicts.join(", ")}</span>
                  </>
                )}
              </span>
            </div>
            <div
              className={status.incomplete.length === 0 ? "check check--good" : "check check--warn"}
            >
              <span className="glyph" aria-hidden="true">
                {status.incomplete.length === 0 ? "✓" : "!"}
              </span>
              <span>
                {status.incomplete.length === 0 ? (
                  <>
                    Incomplete appointments: <b>none</b> in this window
                  </>
                ) : (
                  <>
                    Incomplete appointments: <b>{status.incomplete.length}</b> —{" "}
                    <span className="mono">{status.incomplete.slice(0, 6).join(", ")}</span>
                    {status.incomplete.length > 6 ? " …" : ""}
                  </>
                )}
              </span>
            </div>
            {provisional.length === 0 ? null : (
              <div className="check check--alert">
                <span className="glyph" aria-hidden="true">
                  ✕
                </span>
                <span>
                  <span className="mono">{provisional[0]?.matchNo}</span> (
                  {provisional[0]?.sides}) has an appointed crew against an{" "}
                  <b>unresolved placeholder</b> — a country conflict cannot be ruled out until
                  the pools resolve it.
                  {provisional.length > 1
                    ? ` ${String(provisional.length - 1)} more like it.`
                    : ""}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
      <div className="panel__foot">
        <b>Technical crews only.</b> The figure counts the three slots module 01 appoints. The
        umpiring half of each crew is module 02 and has no store, so counting it would be
        counting three slots and calling them seven.
      </div>
    </section>
  );
}

/* ── §9 `Alerts & Reminders`, and gap N4 ─────────────────────────────────────── */

/**
 * RUN 82G — where the crew half of the alert list stands (second QA, L11): the focus day's own
 * appointments are `read`, still `loading`, or `failed`. `deskAlerts` raises no unfilled-slot or
 * country-conflict alert until they are read; this is how the panel says why the list has none.
 *
 * RUN 82J — the same three words now say where ANY day's own read stands, for Today's Matches and
 * Appointment Status too (third QA, M3), so the type is `derive.ts`'s `DayReadState` under the name
 * this panel has always used.
 */
export type CrewAlertsState = DayReadState;

/** RUN 82G — the panel's line when the day's appointments could not be read. */
export const CREWS_NOT_CHECKED =
  "The appointments for the day in view could not be read, so unfilled slots and country " +
  "conflicts are not listed here.";

/**
 * RUN 82J — what `Nothing Outstanding` says was checked, and no more (third QA, L6).
 *
 * It said `every technical crew on the days in view is filled`, and the crew alerts have only ever
 * looked at the focus day's matches that are still to be played — for an unfilled TO, SJ or TJ, and
 * for a country conflict. So the sentence names that day, and those two checks. Hearings are listed
 * for today and tomorrow on the tournament's clock (RUN 82J, H1), and reports by whether they are
 * started, as the alert list itself raises them.
 */
export function nothingOutstanding(day: string | null, isToday: boolean): string {
  const crews =
    day === null
      ? "the schedule holds no match whose crew could be checked"
      : `every match still to be played ${isToday ? "today" : `on ${shortDate(day)}`} has its TO, ` +
        "SJ and TJ filled and no country conflict flagged";
  return `No hearing is listed for today or tomorrow, ${crews}, and no report is still unstarted.`;
}

/**
 * **RUN 82G — the list waits for the crews, and never claims they are filled when it has not read
 * them.** While the day's appointments are out, the panel draws the loading bars in place of the
 * list: the hearing and report alerts are known, but a list drawn without its crew alerts would be
 * redrawn with them a moment later, and an empty one would say `every technical crew … is filled`
 * about crews nobody has read. When that read fails the panel draws the alerts it has, under a
 * quiet `Not Loaded` line saying unfilled slots and conflicts are not checked — a `status`, since
 * nothing on the page is wrong but the absence of a check.
 */
export function AlertsPanel({
  alerts,
  actioned,
  onAction,
  crews,
  crewDay,
  crewDayIsToday,
}: {
  alerts: readonly DeskAlert[];
  actioned: ReadonlySet<string>;
  onAction: (id: string, done: boolean) => void;
  /** RUN 82G — whether the crew alerts could be derived at all. */
  crews: CrewAlertsState;
  /** RUN 82J — the day the crew alerts are about (the focus day), for `Nothing Outstanding`. */
  crewDay: string | null;
  /** RUN 82J — whether that day is today on the tournament's clock. */
  crewDayIsToday: boolean;
}): React.JSX.Element {
  const open = alerts.filter((alert) => !actioned.has(alert.id));
  const done = alerts.filter((alert) => actioned.has(alert.id));
  return (
    <section className="panel panel--d3" aria-labelledby="alertsTitle">
      <div className="panel__head">
        <h3 className="panel__title" id="alertsTitle">
          Alerts &amp; Reminders
        </h3>
        <span className="spacer" />
        <span className="panel__note">Sorted By Urgency</span>
      </div>

      {crews === "failed" ? (
        <FailBanner error={new Error(CREWS_NOT_CHECKED)} role="status" />
      ) : null}

      {crews === "loading" ? (
        <Skeleton rows={3} />
      ) : alerts.length > 0 ? (
        <ul className="alist">
          {open.map((alert) => (
            <AlertRow key={alert.id} alert={alert} actioned={false} onAction={onAction} />
          ))}
          {done.length === 0 ? null : (
            <li className="alist__group" aria-hidden="false">
              Actioned today
            </li>
          )}
          {done.map((alert) => (
            <AlertRow key={alert.id} alert={alert} actioned onAction={onAction} />
          ))}
        </ul>
      ) : crews === "read" ? (
        <EmptyState title="Nothing Outstanding" detail={nothingOutstanding(crewDay, crewDayIsToday)} />
      ) : null}

      <div className="panel__foot">
        <b>Four of §9&rsquo;s eight alert types are derivable here</b> — hearing scheduled,
        country conflict, unfilled appointment slot, report not yet drafted. Official birthday
        needs a date of birth on the roster read, team colours are shown on the fixture status
        instead, there is no fetch loop to report as failed, and Configuration holds no daily
        marks deadline to approach. The per-type switches of Configuration §07 are not modelled
        either, so every alert above is on.
      </div>
    </section>
  );
}

function AlertRow({
  alert,
  actioned,
  onAction,
}: {
  alert: DeskAlert;
  actioned: boolean;
  onAction: (id: string, done: boolean) => void;
}): React.JSX.Element {
  return (
    <li className={actioned ? `${alert.urgency} is-actioned` : alert.urgency}>
      <span className="bar-ind" aria-hidden="true" />
      <span className="a-txt">
        <b>{alert.lead}</b> {alert.rest}
      </span>
      <span className="a-when">{alert.when}</span>
      <span className="a-act">
        {alert.href === null ? null : <Link to={alert.href}>{alert.hrefLabel} →</Link>}
        <button
          type="button"
          className="gbtn gbtn--sm"
          onClick={() => {
            onAction(alert.id, !actioned);
          }}
        >
          {actioned ? "Reopen" : "Done"}
        </button>
      </span>
    </li>
  );
}

/* ── §9 `Inbox Monitor` ──────────────────────────────────────────────────────── */

export function InboxPanel(): React.JSX.Element {
  return (
    <section className="panel panel--d3" aria-labelledby="inboxTitle">
      <div className="panel__head">
        <h3 className="panel__title" id="inboxTitle">
          Inbox Monitor
        </h3>
        <span className="spacer" />
        <span className="panel__note">Not Connected</span>
      </div>
      <EmptyState
        title="No Mailbox Is Connected"
        detail="§9 reads the tournament mailbox live from Mac Mail on the Technical Delegate's own machine, and triages each message. Nothing in this build reads mail, so there is no last-five list, no fetch to run and no triage to show."
      />
    </section>
  );
}

/* -- Sec. 8 `Cards Listing`, in the rail since RUN 82 (Sec. 8a); drawn from the TMS report since RUN 86 -- */

/** How many players the rail lists before `Show All` - the approved mock draws eight. */
export const CARDS_LISTED_FIRST = 10;

/**
 * RUN 86 - what the two cards panels say when no report is held, by whether the server is
 * reading at all, and whether it has tried. Said in the Technical Delegate's terms: the switch is
 * his to throw. Once a read has been made and none produced a report, the promise of a first read
 * "within a minute or so" is no longer true, and the panel says what is instead (QA L9).
 *
 * Fix round, item 9: each panel says what IT does with the reports - the listing re-sorts them,
 * the average averages them - and the interval is the server's own (`intervalMinutes`), never an
 * assumed five. "The line above" is true in both panels: each draws its failure lines above its
 * empty state.
 */
export function noReportDetail(
  poller: "on" | "off",
  options: {
    attempted?: boolean;
    panel?: "listing" | "average";
    intervalMinutes?: number;
    /** Why the reader, though on, is reading nothing - `GET /cards`'s `pause`. */
    pause?: CardsBody["pause"];
  } = {},
): string {
  const every = everyMinutes(options.intervalMinutes ?? 5);
  const does =
    options.panel === "average"
      ? "this panel averages them per game"
      : "this panel re-sorts them by severity";
  if (poller === "off") {
    return (
      "No TMS cards report has been read on this server: its reader is switched off " +
      `(TMS_CARDS_POLL). Switched on, it reads both competitions' public reports ${every}, and ` +
      `${does}.`
    );
  }
  // Follow-up round, item 1: paused, the reader keeps neither promise below - no read in a minute
  // or so, none again in five - so the empty state points at the line above that says why.
  if (options.pause !== undefined && options.pause !== null) {
    return options.attempted === true
      ? "No read so far has produced a report that passes its checks, and reading is now paused: " +
          "the lines above say why."
      : "No TMS cards report has been read yet: reading is paused, and the line above says why.";
  }
  return options.attempted === true
    ? "No read so far has produced a report that passes its checks, and the line above says " +
        `why. Both competitions' public reports are read again ${every}.`
    : `No TMS cards report has been read yet. Both competitions' public reports are read ${every}, ` +
        "and the first read lands within a minute or so of the server starting.";
}

/** RUN 86 - whether the server has made any read at all, successful or not. */
function attemptedRead(body: CardsBody): boolean {
  return body.competitions.some((competition) => competition.lastRead !== null);
}

/**
 * RUN 86 (QA M3) - the Desk's own read of `GET /cards`, when it has stopped landing: spec 01
 * Sec. 15's stale state, as every other refreshed panel says it (`AsOfStamp`, RUN 82G), in amber,
 * under the report's own As Of. The two stamps are different clocks - when TMS generated the
 * report, and when this Desk last heard from its own server - so this one is named, and drawn only
 * while it fails. It is quiet: the page's one `Refresh Failing` announcer says it aloud.
 */
function CardsDeskRead({
  staleRead,
  testId,
}: {
  staleRead: string | null;
  testId: string;
}): React.JSX.Element | null {
  if (staleRead === null) return null;
  return (
    <p className="cl-deskread">
      <span className="asof asof--failing" data-testid={testId}>
        Desk Read {staleRead.replace(/^As Of /, "")} &middot; Refresh Failing
      </span>
    </p>
  );
}

/** RUN 86 - the report behind the figures: which competition, its heading, and its As Of. */
function CardsSources({
  body,
  now,
  testIdPrefix,
}: {
  body: CardsBody;
  now: Date;
  testIdPrefix: string;
}): React.JSX.Element | null {
  const held = body.competitions.filter((competition) => competition.report !== null);
  if (held.length === 0) return null;
  return (
    <ul className="cl-sources">
      {held.map((competition) => {
        const report = competition.report as NonNullable<typeof competition.report>;
        const stamp = reportAsOf(report, now);
        const overdue = reportIsStale(report, body, now);
        return (
          <li key={competition.competitionId}>
            <b>{competitionLabel(competition.gender)}</b> &middot; {report.heading} &middot;{" "}
            <span
              className={overdue ? "asof asof--failing" : "asof"}
              title={stamp.title}
              data-testid={`${testIdPrefix}-asof-${competition.competitionId}`}
            >
              {stamp.label}
              {/* Follow-up round, item 2a: the reason in words, as the Desk's own stamps say theirs. */}
              {overdue ? <> &middot; Overdue</> : null}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/** RUN 86 - the line a held report carries when nothing is refreshing it any more. */
export const CARDS_READER_OFF =
  "This server's reader is switched off (TMS_CARDS_POLL), so these reports are not being " +
  "refreshed: each is as of the time shown.";

/**
 * RUN 86 - one quiet line per competition whose latest read failed, one when reports are held
 * but the reader is off, and one when the reader is on but paused (fix round 3, item 6b). On screen only, with no live role (QA M2): each line carries the time of
 * the latest failed read, which moves on every five-minute read while TMS is down, and both panels
 * draw the lines - as live regions they were re-announced twice every five minutes. What a screen
 * reader is told is the page's one cards announcer (`cardsAnnouncement`), whose words change only
 * when the set of failing competitions does.
 */
function CardsProblems({ body, now }: { body: CardsBody; now: Date }): React.JSX.Element | null {
  const problems: { id: string; text: string }[] = body.competitions
    .map((competition) => ({ id: competition.competitionId, text: readProblem(competition, now) }))
    .filter((entry): entry is { id: typeof entry.id; text: string } => entry.text !== null);
  const held = body.competitions.some((competition) => competition.report !== null);
  if (body.poller === "off" && held) problems.unshift({ id: "reader", text: CARDS_READER_OFF });
  // Fix round 3, item 6b: why the reader, though on, read nothing - one short line.
  const paused = body.poller === "on" ? pauseLine(body.pause) : null;
  if (paused !== null) problems.unshift({ id: "paused", text: paused });
  if (problems.length === 0) return null;
  return (
    <>
      {problems.map((problem) => (
        <p className="cl-problem" key={problem.id} data-testid={`cards-problem-${problem.id}`}>
          {problem.text}
        </p>
      ))}
    </>
  );
}

/**
 * RUN 86 - one colour of one player's tally: the mock's `R:1`, and `1 red` for a screen reader,
 * with a comma after every colour but the last, so the three are heard as three (QA M6).
 */
function Tally({
  colour,
  count,
}: {
  colour: "r" | "y" | "g";
  count: number;
}): React.JSX.Element {
  const word = colour === "r" ? "red" : colour === "y" ? "yellow" : "green";
  return (
    <span className={count === 0 ? "cardtally cl-zero" : "cardtally"}>
      <i className={`ci ci--${colour}`} aria-hidden="true" />
      <span aria-hidden="true">
        {colour.toUpperCase()}:{count}
      </span>
      <span className="sr-only">
        {count} {word}
        {colour === "g" ? "" : ", "}
      </span>
    </span>
  );
}

/**
 * Sec. 8's Cards Listing - the TMS cards report, re-sorted by severity - in its rail form (Sec. 8a).
 *
 * **RUN 86 - drawn from the report.** `GET /cards` serves both competitions' public TMS reports
 * as the server last read them, and the panel lists every carded player in the mock's two-part
 * row - `#shirt NAME` with the team tag, then `R:n Y:n G:n` with zero counts dimmed - in exactly
 * the order the head promises: most reds, then most yellows, then most greens. The team tag is
 * the competition's colour, navy for the Men's and pink for the Women's, and says which in words
 * for a screen reader.
 *
 * **What it cannot show.** The mock's second line, the latest incident (`Red M06 - 52' Physical
 * play`), is not in the TMS report - it carries counts and nothing else - so it is not drawn, and
 * the foot says so rather than leaving a gap to be read as a fault.
 *
 * **When there is nothing to list** it says why, in order of what the Technical Delegate can do
 * about it: the reader is switched off; no read has landed yet; the latest read failed (and the
 * report still shown is named with its time); or the report lists nobody.
 */
export const CardsListingPanel = memo(function CardsListingPanel({
  cards,
  now,
  staleRead = null,
}: {
  /** RUN 86 - the page's `GET /cards` read, refreshed on the Desk's five-minute key. */
  cards: RefreshingAsyncState<CardsBody>;
  /**
   * RUN 86 - the clock the panel reads the reports against: the SERVER's (`servedClock`, follow-up
   * round item 2b), as the page works it out each minute - for whether a report's As Of needs its
   * date, and whether it is overdue.
   */
  now: Date;
  /**
   * RUN 86 (QA M3) - the Desk's own `As Of` for that read while it is more than ten minutes old
   * (`readStamp(...).failing`), and null while it is not. A string, so that `memo` sees a change
   * only when there is one: the page renders every second, and this panel does not need to.
   */
  staleRead?: string | null;
}): React.JSX.Element {
  const [showAll, setShowAll] = useState(false);
  return (
    <section className="panel panel--d3 cardslist" aria-labelledby="cardsListTitle">
      <div className="panel__head">
        <h3 className="panel__title" id="cardsListTitle">
          Cards Listing
        </h3>
        <span className="spacer" />
        <span className="kicker">TMS Cards Report</span>
      </div>
      <p className="cl-subnote">
        Tournament To Date &middot; Both Competitions &middot; Sorted By Red, Then Yellow, Then Green
      </p>
      {cards.status === "loading" ? <Skeleton rows={3} /> : null}
      {cards.status === "error" ? (
        <FailBanner
          error={new Error("The cards reports held on this server could not be read, so nothing is listed here.")}
          role="status"
        />
      ) : null}
      {cards.status === "ready" ? (
        <CardsListingBody
          body={cards.data}
          now={now}
          staleRead={staleRead}
          showAll={showAll}
          onShowAll={() => {
            setShowAll((current) => !current);
          }}
        />
      ) : null}
      <div className="panel__foot cl-foot">
        <b>Counts only.</b> The TMS cards report lists each carded player&rsquo;s red, yellow and
        green cards for the tournament to date; it carries no match, minute or offence, so the
        latest incident is not shown. Both competitions&rsquo; reports are read{" "}
        {cards.status === "ready" ? everyMinutes(cards.data.intervalMinutes) : "regularly"} on the
        Games&rsquo; match days, up to a daily ceiling of requests, when the server&rsquo;s reader
        is on, and a report that does not add up is never shown.
      </div>
    </section>
  );
});

function CardsListingBody({
  body,
  now,
  staleRead,
  showAll,
  onShowAll,
}: {
  body: CardsBody;
  now: Date;
  staleRead: string | null;
  showAll: boolean;
  onShowAll: () => void;
}): React.JSX.Element {
  const rows = cardsListing(body);
  const held = body.competitions.some((competition) => competition.report !== null);
  const visible = showAll ? rows : rows.slice(0, CARDS_LISTED_FIRST);
  return (
    <>
      <CardsSources body={body} now={now} testIdPrefix="cards-list" />
      <CardsDeskRead staleRead={staleRead} testId="cards-list-deskread" />
      <CardsProblems body={body} now={now} />
      {!held ? (
        <div className="emptystate">
          <b>No Cards Report Is Available</b>
          {noReportDetail(body.poller, {
            attempted: attemptedRead(body),
            panel: "listing",
            intervalMinutes: body.intervalMinutes,
            pause: body.pause,
          })}
        </div>
      ) : rows.length === 0 ? (
        <div className="emptystate">
          <b>No Carded Player Yet</b>
          The TMS reports held list no player with a card.
        </div>
      ) : (
        <>
          <ul
            className="cl-rail"
            aria-label="Players who have received cards, ranked by red, then yellow, then green cards"
          >
            {visible.map((row) => (
              <li key={row.key} data-testid={`cards-row-${row.key}`}>
                {/*
                 * QA M6 - the words a screen reader hears are `Shirt 9, SUAREZ Fictional, URU,
                 * Men's; 1 red, 0 yellow, 2 green`: the separators are hidden from sight, and the
                 * `#9` a sighted reader sees is hidden from the reader, which hears `Shirt 9`.
                 */}
                <span className="clr-player">
                  <span className="clr-shirt">
                    <span aria-hidden="true">#{row.shirtNumber}</span>
                    <span className="sr-only">Shirt {row.shirtNumber}, </span>
                  </span>
                  {/*
                   * Security review, LOW-2: the name in a bidirectional isolate, so that nothing in
                   * it - the parser already refuses format characters - could reorder what follows.
                   */}
                  <bdi className="clr-name">{row.playerName}</bdi>
                  <span className={row.gender === "w" ? "clr-team clr-team--w" : "clr-team"}>
                    <span className="sr-only">, </span>
                    {row.teamCode}
                    <span className="sr-only">, {competitionLabel(row.gender)}; </span>
                  </span>
                </span>
                <span className="clr-counts">
                  <Tally colour="r" count={row.red} />
                  <Tally colour="y" count={row.yellow} />
                  <Tally colour="g" count={row.green} />
                </span>
              </li>
            ))}
          </ul>
          {rows.length > CARDS_LISTED_FIRST ? (
            <div className="cl-more">
              <button type="button" className="gbtn gbtn--sm" aria-expanded={showAll} onClick={onShowAll}>
                {showAll ? "Show Fewer" : `Show All ${String(rows.length)} Players`}
              </button>
            </div>
          ) : null}
        </>
      )}
    </>
  );
}

/* ── §9 `Useful Links` ───────────────────────────────────────────────────────── */

/** §9's two links, verbatim. */
export const USEFUL_LINKS: readonly { label: string; href: string }[] = [
  {
    label: "FIH Doc",
    href: "https://www.fih.hockey/about-fih/official-documents/sport-event-resources",
  },
  { label: "TMS", href: "https://tms.fih.ch/admin/content/competitions" },
];

export function UsefulLinksPanel(): React.JSX.Element {
  return (
    <section className="panel panel--d4" aria-labelledby="uselinksTitle">
      <div className="panel__head">
        <h3 className="panel__title" id="uselinksTitle">
          Useful Links
        </h3>
        <span className="spacer" />
        <span className="panel__note">Opens In New Tab</span>
      </div>
      <div className="uselinks">
        {USEFUL_LINKS.map((link) => (
          <a
            key={link.label}
            className="linkbtn"
            href={link.href}
            target="_blank"
            rel="noopener noreferrer"
          >
            {link.label} <span aria-hidden="true">↗</span>
          </a>
        ))}
      </div>
    </section>
  );
}

/* ── §9 `Tournament Officials` ───────────────────────────────────────────────── */

export function OfficialsDonutPanel({ donut }: { donut: DonutView }): React.JSX.Element {
  return (
    <section className="panel panel--d4 stat" aria-labelledby="statOffTitle">
      <div className="panel__head">
        <h3 className="panel__title" id="statOffTitle">
          Tournament Officials
        </h3>
      </div>
      <div className="stat__body">
        {donut.total === 0 ? (
          <EmptyState
            title="No Roster Loaded"
            detail="Import the tournament's officials and the six role categories appear here."
          />
        ) : (
          <div className="donutrow">
            <div className="donutwrap">
              <svg className="donut" viewBox="0 0 42 42" role="img" aria-label={donut.ariaLabel}>
                <g transform="rotate(-90 21 21)" fill="none" strokeWidth="5.4">
                  {donut.segments
                    .filter((segment) => segment.count > 0)
                    .map((segment) => (
                      <circle
                        key={segment.label}
                        cx="21"
                        cy="21"
                        r="15.9155"
                        stroke={segment.colour}
                        strokeDasharray={segment.dash}
                        strokeDashoffset={segment.offset}
                      >
                        <title>
                          {segment.label} — {segment.count} of {donut.total}
                        </title>
                      </circle>
                    ))}
                </g>
              </svg>
              <div className="donut__center">
                <span className="num">{donut.total}</span>
              </div>
            </div>
            <ul className="donutlegend donutlegend--2col">
              {donut.segments.map((segment) => (
                <li key={segment.label}>
                  <i style={{ background: segment.colour }} />
                  <b>{segment.count}</b> {segment.label}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <div className="panel__foot">
        <b>{ROLE_SEGMENTS.length} categories, and TDs is plural</b> — a big event may run one
        Technical Delegate per competition, so the data model carries more than one.
      </div>
    </section>
  );
}

/* -- Sec. 9 `Cards Average Per Game` - drawn from the TMS reports since RUN 86 -------------- */

function plural(count: number, noun: string): string {
  return `${String(count)} ${noun}${count === 1 ? "" : "s"}`;
}

/**
 * Sec. 9's Cards Average Per Game: `Green - Yellow - Red`, one decimal, both competitions.
 *
 * **RUN 86 - the per-game figures are the reports' own.** Each colour's cards in both reports held,
 * over the games both reports say they were counted after (`after M games played`); the sub-line
 * says how many cards in how many games and the total per game, and each competition's own
 * figures and As Of are listed under it. Where only one report has been read the figures are that
 * competition's, and the sub-line says so rather than calling them both competitions'. Sec. 9's
 * trend line draws the combined average after each report the server stored, once there are two.
 *
 * **Never a 0.0 that is not a measurement** (Sec. 15): with no report the panel keeps its named
 * empty state, and with no game played it says so.
 */
export const CardsAveragePanel = memo(function CardsAveragePanel({
  cards,
  now,
  staleRead = null,
}: {
  /** RUN 86 - the page's `GET /cards` read - the same read Cards Listing draws. */
  cards: RefreshingAsyncState<CardsBody>;
  /** As Cards Listing's: the server's clock, as the page works it out each minute. */
  now: Date;
  /** RUN 86 (QA M3) - as Cards Listing's: the Desk's own `As Of` while it fails, else null. */
  staleRead?: string | null;
}): React.JSX.Element {
  return (
    <section className="panel panel--d4 stat cardsavg" aria-labelledby="statCardsTitle">
      <div className="panel__head">
        <h3 className="panel__title" id="statCardsTitle">
          Cards Average Per Game
        </h3>
      </div>
      <div className="stat__body">
        {cards.status === "loading" ? <Skeleton rows={2} /> : null}
        {cards.status === "error" ? (
          <FailBanner
            error={new Error("The cards reports held on this server could not be read, so there is no average to state.")}
            role="status"
          />
        ) : null}
        {cards.status === "ready" ? (
          <CardsAverageBody body={cards.data} now={now} staleRead={staleRead} />
        ) : null}
      </div>
    </section>
  );
});

function CardsAverageBody({
  body,
  now,
  staleRead,
}: {
  body: CardsBody;
  now: Date;
  staleRead: string | null;
}): React.JSX.Element {
  const averages = cardsAverages(body);
  // Fix round, item 9: the listing's order - the sources, the Desk's own stale read and the
  // failure lines first, then the empty state - so that "the line above" is above.
  if (averages === null) {
    return (
      <>
        <CardsDeskRead staleRead={staleRead} testId="cards-avg-deskread" />
        <CardsProblems body={body} now={now} />
        <EmptyState
          title="No Card Data Yet"
          detail={noReportDetail(body.poller, {
            attempted: attemptedRead(body),
            panel: "average",
            intervalMinutes: body.intervalMinutes,
            pause: body.pause,
          })}
        />
      </>
    );
  }
  if (averages.perGame === null) {
    return (
      <>
        <CardsSources body={body} now={now} testIdPrefix="cards-avg" />
        <CardsDeskRead staleRead={staleRead} testId="cards-avg-deskread" />
        <CardsProblems body={body} now={now} />
        <EmptyState
          title="No Game Played Yet"
          detail="The TMS cards reports held say no game has been played, so there is no average to state."
        />
      </>
    );
  }
  const perGame = averages.perGame;
  const scope =
    averages.missing.length === 0
      ? "Both competitions"
      : `${averages.competitions.map((entry) => competitionLabel(entry.gender)).join(" and ")} only ` +
        `\u2014 the ${averages.missing.map(competitionLabel).join(" and ")} report has not been read`;
  const trend = cardsTrend(body);
  const top = Math.max(...trend.flatMap((point) => [point.red, point.yellow, point.green]), 0);
  return (
    <>
      <div className="cardsrow">
        <span className="cardstat">
          <svg viewBox="0 0 12 12" className="cardicon" aria-hidden="true">
            <polygon points="6,1 11.2,10 0.8,10" fill="#1d7a46" />
          </svg>
          Green{" "}
          <b className="num" data-testid="cards-avg-green">
            {oneDecimal(perGame.green)}
          </b>
        </span>
        <span className="cardstat">
          <svg viewBox="0 0 12 12" className="cardicon" aria-hidden="true">
            <rect x="1.2" y="1.2" width="9.6" height="9.6" fill="#d9a800" />
          </svg>
          Yellow{" "}
          <b className="num" data-testid="cards-avg-yellow">
            {oneDecimal(perGame.yellow)}
          </b>
        </span>
        <span className="cardstat">
          <svg viewBox="0 0 12 12" className="cardicon" aria-hidden="true">
            <circle cx="6" cy="6" r="5" fill="#b3261e" />
          </svg>
          Red{" "}
          <b className="num" data-testid="cards-avg-red">
            {oneDecimal(perGame.red)}
          </b>
        </span>
      </div>
      <div className="stat__sub" data-testid="cards-avg-sub">
        {scope} &middot; {plural(averages.cards.total, "card")} in {plural(averages.games, "game")}{" "}
        &middot; <b>{oneDecimal(perGame.total)}</b> a game
      </div>
      {trend.length < 2 ? null : (
        <svg
          className="spark spark--cards"
          viewBox="0 0 100 30"
          preserveAspectRatio="none"
          aria-hidden="true"
          data-testid="cards-avg-trend"
        >
          <polyline style={{ stroke: "#1d7a46" }} points={sparkPoints(trend.map((p) => p.green), top)} />
          <polyline style={{ stroke: "#d9a800" }} points={sparkPoints(trend.map((p) => p.yellow), top)} />
          <polyline style={{ stroke: "#b3261e" }} points={sparkPoints(trend.map((p) => p.red), top)} />
        </svg>
      )}
      <ul className="cardsavg__comps">
        {averages.competitions.map((entry) => {
          const report = body.competitions.find(
            (competition) => competition.competitionId === entry.competitionId,
          )?.report;
          const stamp = report === null || report === undefined ? null : reportAsOf(report, now);
          const overdue = report !== null && report !== undefined && reportIsStale(report, body, now);
          return (
            <li key={entry.competitionId} data-testid={`cards-avg-${entry.competitionId}`}>
              <b>{competitionLabel(entry.gender)}</b> &middot; {plural(entry.games, "game")}
              {entry.perGame === null ? null : (
                <>
                  {" "}
                  &middot; <ColourLetter colour="g" /> {oneDecimal(entry.perGame.green)} &middot;{" "}
                  <ColourLetter colour="y" /> {oneDecimal(entry.perGame.yellow)} &middot;{" "}
                  <ColourLetter colour="r" /> {oneDecimal(entry.perGame.red)}
                </>
              )}
              {stamp === null ? null : (
                <>
                  {" "}
                  &middot;{" "}
                  <span
                    className={overdue ? "asof asof--failing" : "asof"}
                    title={stamp.title}
                    data-testid={`cards-avg-asof-${entry.competitionId}`}
                  >
                    {stamp.label}
                    {overdue ? <> &middot; Overdue</> : null}
                  </span>
                </>
              )}
            </li>
          );
        })}
      </ul>
      <CardsDeskRead staleRead={staleRead} testId="cards-avg-deskread" />
      <CardsProblems body={body} now={now} />
    </>
  );
}

/**
 * RUN 86 (QA L8) - `G`, `Y` or `R` on screen, and the colour's name to a screen reader, which
 * would otherwise say "why" for the yellow.
 */
function ColourLetter({ colour }: { colour: "r" | "y" | "g" }): React.JSX.Element {
  const word = colour === "r" ? "red" : colour === "y" ? "yellow" : "green";
  return (
    <>
      <span aria-hidden="true">{colour.toUpperCase()}</span>
      <span className="sr-only">{word}</span>
    </>
  );
}

/* ── §9 `Pool Stage Progress` ────────────────────────────────────────────────── */

export function PoolProgressPanel({ progress }: { progress: PoolProgress }): React.JSX.Element {
  return (
    <section className="panel panel--d4 stat" aria-labelledby="statPoolTitle">
      <div className="panel__head">
        <h3 className="panel__title" id="statPoolTitle">
          Pool Stage Progress
        </h3>
      </div>
      <div className="stat__body">
        {progress.poolTotal === 0 ? (
          <EmptyState
            title="No Pool Matches"
            detail="No fixture in this schedule names a pool, so there is no pool stage to measure."
          />
        ) : (
          <>
            <div className="stat__value">
              <span className="num" data-testid="pool-complete">
                {progress.poolComplete}
              </span>
              <span className="unit"> / {progress.poolTotal}</span>
            </div>
            <div className="stat__sub">
              Pool matches complete · Knockouts{" "}
              <b>
                {progress.knockoutComplete} / {progress.knockoutTotal}
              </b>
            </div>
            <div className="sparkbars" aria-hidden="true">
              {progress.bars.map((height, index) => (
                <i key={index} style={{ height: `${String(Math.max(height, 4))}%` }} />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

/* ── §9 `Reports Outstanding` ────────────────────────────────────────────────── */

export function ReportsOutstandingPanel({
  outstanding,
  total,
}: {
  outstanding: number;
  total: number;
}): React.JSX.Element {
  const segments = 10;
  const lit = total === 0 ? 0 : Math.round((outstanding / total) * segments);
  return (
    <section className="panel panel--d4 stat" aria-labelledby="statRepTitle">
      <div className="panel__head">
        <h3 className="panel__title" id="statRepTitle">
          Reports Outstanding
        </h3>
      </div>
      <div className="stat__body">
        <div className="stat__value num" data-testid="reports-outstanding">
          {outstanding}
        </div>
        <div className="stat__sub">
          Performance reports not yet drafted, of <b>{total}</b> on the panel. §9&rsquo;s{" "}
          <i>due today 18:00</i> comes from Configuration&rsquo;s <i>Daily Marks Deadline</i>,
          which is not a field the configuration store holds.
        </div>
        <div className="minibar" aria-hidden="true">
          {Array.from({ length: segments }, (_, index) => (
            <span key={index} className={index < lit ? "on" : undefined} />
          ))}
        </div>
      </div>
    </section>
  );
}
